import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

type LatestDoc = {
  doc_type: "terms" | "privacy";
  version: string;
  title: string;
  content_md: string;
};

// Updated policy: require a ONE-TIME acceptance of Terms + Privacy.
// If the user has ever accepted both doc_types (any version), they are never re-prompted.
// We still fetch current docs for potential UI uses, but gating ignores versions.
// Fail-open on errors or if no docs configured (do not lock users out).
export function useLegalAcceptanceCheck() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [needsAcceptance, setNeedsAcceptance] = useState(false);
  const [latestDocs, setLatestDocs] = useState<LatestDoc[]>([]);

  const check = useCallback(async () => {
    if (!user?.id) {
      setNeedsAcceptance(false);
      setLatestDocs([]);
      setLoading(false);
      return;
    }

    try {
      // Fetch current (is_current) docs for display purposes only.
      const { data: docs, error: docsErr } = await supabase
        .from("legal_documents")
        .select("doc_type, version, title, content_md")
        .eq("is_current", true);
      if (docsErr) throw docsErr;

      if (!docs || docs.length === 0) {
        // Nothing configured -> do not block.
        setNeedsAcceptance(false);
        setLatestDocs([]);
        return;
      }
      setLatestDocs(docs as LatestDoc[]);

      // One-time acceptance logic: user must have at least one acceptance row
      // for EACH required doc_type (terms + privacy) regardless of version.
      const { data: acceptRows, error: acceptErr } = await supabase
        .from("user_legal_acceptances")
        .select("doc_type")
        .eq("user_id", user.id)
        .in("doc_type", ["terms", "privacy"]);
      if (acceptErr) throw acceptErr;

      const acceptedTypes = new Set((acceptRows || []).map((r) => r.doc_type));
      const hasAll = acceptedTypes.has("terms") && acceptedTypes.has("privacy");
      setNeedsAcceptance(!hasAll);
    } catch {
      // Fail-open on error
      setNeedsAcceptance(false);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    check();
  }, [check]);

  return { loading, needsAcceptance, latestDocs, recheck: check };
}
